import * as React from 'react';

import {
  enumPurchaseOrderSortOption,
  PurchaseOrderSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  useMemoDataGridRows,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2, LucideX } from 'lucide-react';

import { purchaseOrderCloseAtom } from '../../_graphql/mutationPurchaseOrderClose';
import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/purchaseOrdersList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { PurchaseOrderRecord } from '../../types/purchaseOrder';
import { LoadCreate } from '../loads/LoadCreate';

import { PurchaseOrderWizard } from './_PurchaseOrderWizard';
import { ClosePurchaseOrderDialog } from './ClosePurchaseOrderDialog';
import { usePurchaseOrdersListColumns } from './hooks/usePurchaseOrdersListColumns';
import { PurchaseOrderFilters } from './PurchaseOrderListFilters';
import { VoidPurchaseOrderDialog } from './VoidPurchaseOrderDialog';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: PurchaseOrderSortOption;
    desc: PurchaseOrderSortOption;
  }
> = {
  supplier: {
    asc: enumPurchaseOrderSortOption.AccountAsc,
    desc: enumPurchaseOrderSortOption.AccountDesc,
  },
  status: {
    asc: enumPurchaseOrderSortOption.StatusAsc,
    desc: enumPurchaseOrderSortOption.StatusDesc,
  },
  endDate: {
    asc: enumPurchaseOrderSortOption.EndDateAsc,
    desc: enumPurchaseOrderSortOption.EndDateDesc,
  },
  createdOn: {
    asc: enumPurchaseOrderSortOption.CreatedAtAsc,
    desc: enumPurchaseOrderSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ PURCHASE ORDERS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const PurchaseOrderListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remotePurchaseOrders,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);

  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isWizardOpen, setIsWizardOpen] = useAtom(state.$isWizardOpen);
  const [draftPurchaseOrderId, setDraftPurchaseOrderId] = useAtom(
    state.$draftPurchaseOrderId,
  );
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useAtom(
    state.$isLoadDialogOpen,
  );
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useAtom(
    state.$selectedPurchaseOrder,
  );
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useAtom(
    state.$isCloseDialogOpen,
  );

  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const deletePurchaseOrder = useSetAtom(state.$deletePurchaseOrder);
  const closePurchaseOrder = useSetAtom(purchaseOrderCloseAtom({}));

  const toast = useToast();

  const purchaseOrders = data?.purchaseOrders ?? [];
  const total = data?.totalCount ?? 0;
  const selectedRows = useMemoDataGridRows(purchaseOrders, selectedKeys);
  const facilities = facilitiesData?.facilities ?? [];

  // Callbacks
  const handleRefetchPurchaseOrders = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handlePurchaseOrderClick = React.useCallback(
    (purchaseOrder: PurchaseOrderRecord) => {
      navigate(tradeRoutePaths.purchaseOrderDetail, {
        params: { id: purchaseOrder.id },
      });
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    setDraftPurchaseOrderId(null);
    setIsWizardOpen(true);
  }, [setDraftPurchaseOrderId, setIsWizardOpen]);

  const handleWizardSuccess = React.useCallback(() => {
    setIsWizardOpen(false);
    setDraftPurchaseOrderId(null);
    refetch();
    toast.success(t.purchaseOrdersView.wizard.messages.createSuccess);
  }, [t, toast, setDraftPurchaseOrderId, setIsWizardOpen, refetch]);

  const handleWizardOpenChange = React.useCallback(
    (open: boolean) => {
      setIsWizardOpen(open);
      if (!open) {
        setDraftPurchaseOrderId(null);
      }
    },
    [setDraftPurchaseOrderId, setIsWizardOpen],
  );

  const handleSetIsLoadDialogOpen = React.useCallback(
    (open: boolean, purchaseOrderId?: string) => {
      if (open) {
        setSelectedPurchaseOrder(
          purchaseOrderId
            ? ({ id: purchaseOrderId } as PurchaseOrderRecord)
            : null,
        );
      }
      setIsLoadDialogOpen(open);
    },
    [setSelectedPurchaseOrder, setIsLoadDialogOpen],
  );

  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);

  const commonCellProps = React.useCallback(
    (purchaseOrder: PurchaseOrderRecord) => ({
      className: 'cursor-pointer',
      onDoubleClick: () => handlePurchaseOrderClick(purchaseOrder),
    }),
    [handlePurchaseOrderClick],
  );

  const handleVoidPurchaseOrder = React.useCallback(
    (purchaseOrderId?: string | null) => {
      if (purchaseOrderId != null) {
        const purchaseOrderToVoid = purchaseOrders.find(
          (purchaseOrder) => purchaseOrder.id === purchaseOrderId,
        );
        if (purchaseOrderToVoid == null) {
          return;
        }
        setSelectedPurchaseOrder(purchaseOrderToVoid);
      }
      setIsVoidDialogOpen(true);
    },
    [purchaseOrders, setSelectedPurchaseOrder, setIsVoidDialogOpen],
  );

  const handleVoidConfirm = React.useCallback(
    async (purchaseOrderId: string) => {
      try {
        await deletePurchaseOrder({
          id: purchaseOrderId,
        });

        toast.success(t.purchaseOrderDetail.messages.voided);
        setSelectedPurchaseOrder(null);
        setIsVoidDialogOpen(false);
        handleRefetchPurchaseOrders();
      } catch (error) {
        toast.error(
          `Failed to void purchase order: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        throw error;
      }
    },
    [
      deletePurchaseOrder,
      t,
      toast,
      setSelectedPurchaseOrder,
      setIsVoidDialogOpen,
      handleRefetchPurchaseOrders,
    ],
  );

  const handleClosePurchaseOrder = React.useCallback(
    (purchaseOrderId: string) => {
      const purchaseOrderToClose = purchaseOrders.find(
        (purchaseOrder) => purchaseOrder.id === purchaseOrderId,
      );
      if (purchaseOrderToClose == null) {
        return;
      }
      setSelectedPurchaseOrder(purchaseOrderToClose);
      setIsCloseDialogOpen(true);
    },
    [purchaseOrders, setSelectedPurchaseOrder, setIsCloseDialogOpen],
  );

  const handleClosePurchaseOrderConfirm = React.useCallback(
    async (purchaseOrderId: string, notes?: string) => {
      try {
        await closePurchaseOrder({ id: purchaseOrderId, notes });

        toast.success(t.purchaseOrderDetail.messages.closed);
        setSelectedPurchaseOrder(null);
        setIsCloseDialogOpen(false);
        handleRefetchPurchaseOrders();
      } catch (error) {
        toast.error(
          `Failed to close purchase order: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    },
    [
      closePurchaseOrder,
      t,
      toast,
      handleRefetchPurchaseOrders,
      setSelectedPurchaseOrder,
      setIsCloseDialogOpen,
    ],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumPurchaseOrderSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }

      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCloseLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
    setSelectedPurchaseOrder(null);
  }, [setIsLoadDialogOpen, setSelectedPurchaseOrder]);

  const handleSuccessLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, [setIsLoadDialogOpen]);

  // Other data hooks
  const columns = usePurchaseOrdersListColumns({
    facilities,
    commonCellProps,
    setIsLoadDialogOpen: handleSetIsLoadDialogOpen,
    setSelectedPurchaseOrder,
    onVoid: handleVoidPurchaseOrder,
    onClose: handleClosePurchaseOrder,
    onRefetch: handleRefetchPurchaseOrders,
  });

  const emptyPanelProps = {
    title: t.purchaseOrdersView.emptyState.title,
    description: (
      <span>
        {t.purchaseOrdersView.emptyState.subtitle(
          <strong>{t.purchaseOrdersView.emptyState.action}</strong>,
        )}
      </span>
    ),
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.purchaseOrdersView.toolbar.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <div className="@PurchaseOrderListView flex h-full flex-col">
      <ListScreenLayout
        filters={<PurchaseOrderFilters />}
        actions={
          <Button
            onPress={handleCreateClick}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.purchaseOrdersView.toolbar.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
        className="@PurchaseOrderListView"
      >
        <DataGrid<PurchaseOrderRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={purchaseOrders}
          rowKey="id"
          aria-label={t.purchaseOrdersView.table.aria}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.purchaseOrdersView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.purchaseOrdersView.toolbar.selected}
          </div>
          <div className="flex flex-row items-center gap-2">
            <Button
              variant="outline"
              icon={<LucideTrash2 />}
              isDisabled={selectedRows.length < 1}
              onPress={() => handleVoidPurchaseOrder()}
            >
              {t.purchaseOrdersView.toolbar.delete(selectedRows.length)}
            </Button>
            <LucideX
              className="size-4 cursor-pointer text-neutral/80"
              onClick={handleClearSelection}
            />
          </div>
        </div>
      </FloatingToolbar>

      <PurchaseOrderWizard
        isOpen={isWizardOpen}
        onOpenChange={handleWizardOpenChange}
        onSuccess={handleWizardSuccess}
        draftPurchaseOrderId={draftPurchaseOrderId ?? undefined}
      />

      <LoadCreate
        isOpen={isLoadDialogOpen}
        onClose={handleCloseLoadDialog}
        onSuccess={handleSuccessLoadDialog}
      />

      {selectedPurchaseOrder && isCloseDialogOpen && (
        <ClosePurchaseOrderDialog
          purchaseOrderId={selectedPurchaseOrder.id}
          onClose={handleClosePurchaseOrderConfirm}
          onCancel={() => {
            setSelectedPurchaseOrder(null);
            setIsCloseDialogOpen(false);
          }}
        />
      )}

      {selectedPurchaseOrder && isVoidDialogOpen && (
        <VoidPurchaseOrderDialog
          purchaseOrderId={selectedPurchaseOrder.id}
          onVoid={handleVoidConfirm}
          onCancel={() => {
            setSelectedPurchaseOrder(null);
            setIsVoidDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};
